import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Mail, Copy, ShieldCheck } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const ROLE_LABELS = {
  admin: { en: "Administrator", ar: "مدير" },
  staff: { en: "Staff", ar: "موظف" },
  client: { en: "Member", ar: "عضو" },
};

export default function AdminStaff() {
  const { lang } = useI18n();
  const { user, loading } = useAuth();
  const isAr = lang === "ar";

  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", role: "staff" });
  const [busy, setBusy] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);

  const t = useMemo(() => ({
    eyebrow: isAr ? "إدارة الفريق" : "Staff Management",
    title: isAr ? "إنشاء حسابات للموظفين." : "Provision staff accounts.",
    lead: isAr
      ? "أنشئ حسابات لزملائك في مجموعة الدخيل. سيتم توليد كلمة مرور مؤقتة وإرسال بيانات الدخول إلى بريد كل موظف، مع فتح وضع المحترفين في «اسأل سامي» لهم."
      : "Create accounts for your colleagues at Aldakheel. A temporary password is generated and credentials are emailed to each new user — and they unlock professional mode in 'Ask Sami'.",
    create: isAr ? "إضافة موظف" : "Add staff member",
    name: isAr ? "الاسم الكامل" : "Full name",
    email: isAr ? "البريد الإلكتروني" : "Email",
    role: isAr ? "الدور" : "Role",
    submit: isAr ? "إنشاء وإرسال" : "Create & email",
    list: isAr ? "الفريق الحالي" : "Current team",
    listEmpty: isAr ? "لا يوجد موظفون بعد." : "No staff users yet.",
    delete: isAr ? "حذف" : "Delete",
    confirmDelete: isAr ? "حذف هذا الحساب؟" : "Delete this account?",
    createdHeading: isAr ? "تم الإنشاء بنجاح" : "Account created",
    tempPassword: isAr ? "كلمة المرور المؤقتة" : "Temporary password",
    emailSent: isAr ? "تم إرسال البريد." : "Email sent.",
    emailSkipped: isAr
      ? "لم يُرسَل البريد (لم يتم تكوين Resend) — يُرجى نسخ بيانات الدخول وإرسالها يدويًا."
      : "Email skipped (Resend not configured) — copy the credentials and share them manually.",
    copy: isAr ? "نسخ" : "Copy",
    copied: isAr ? "تم النسخ" : "Copied",
    err: isAr ? "حدث خطأ. حاول مرة أخرى." : "Something went wrong. Try again.",
  }), [isAr]);

  const load = useCallback(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (loading) return <div className="p-20 text-center text-slate-500 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setBusy(true);
    setLastCreated(null);
    try {
      const { data } = await api.post("/admin/users/create", {
        ...form,
        language: lang,
      });
      setLastCreated(data);
      setForm({ name: "", email: "", role: "staff" });
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.err);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      load();
    } catch {
      toast.error(t.err);
    }
  };

  const copyCreds = async () => {
    if (!lastCreated) return;
    const text = `${lastCreated.email}\n${lastCreated.temporary_password}`;
    await navigator.clipboard.writeText(text);
    toast.success(t.copied);
  };

  return (
    <div data-testid="admin-staff-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-6">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight">
            {t.title}
          </h1>
        </div>
        <div className="lg:col-span-6 lg:pt-3">
          <p className="text-lg text-slate-600 leading-relaxed">{t.lead}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* CREATE */}
        <form onSubmit={onCreate} className="lg:col-span-5 space-y-5" data-testid="staff-create-form">
          <div className="border border-slate-200 bg-white p-6 lg:p-8 space-y-5">
            <div className="flex items-center gap-2">
              <Plus className="w-4 h-4 text-slate-500" />
              <p className="eyebrow">{t.create}</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.name}</Label>
              <Input
                required dir="auto"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="staff-name"
                className="mt-2 rounded-sm h-11"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.email}</Label>
              <Input
                type="email" required dir="auto"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="staff-email"
                className="mt-2 rounded-sm h-11"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.role}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-role" className="mt-2 rounded-sm h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">{ROLE_LABELS.staff[lang]}</SelectItem>
                  <SelectItem value="admin">{ROLE_LABELS.admin[lang]}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              type="submit" disabled={busy}
              data-testid="staff-submit"
              className="rounded-sm h-12 w-full bg-slate-900 hover:bg-slate-800 text-white"
            >
              {busy ? (
                <><Loader2 className="w-4 h-4 me-2 animate-spin" /> {t.submit}</>
              ) : (
                <><Mail className="w-4 h-4 me-2" /> {t.submit}</>
              )}
            </Button>
          </div>

          {lastCreated && (
            <div className="border border-amber-200 bg-amber-50 p-6" data-testid="staff-last-created">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-4 h-4 text-amber-700" />
                <p className="eyebrow text-amber-800">{t.createdHeading}</p>
              </div>
              <div className="text-sm space-y-1">
                <p><span className="text-slate-500">{t.email}:</span> <span className="font-medium">{lastCreated.email}</span></p>
                <p>
                  <span className="text-slate-500">{t.tempPassword}:</span>{" "}
                  <code className="font-mono bg-white border border-amber-200 px-2 py-0.5">{lastCreated.temporary_password}</code>
                </p>
                <p className="pt-2 text-xs text-slate-600">
                  {lastCreated.email_sent ? t.emailSent : t.emailSkipped}
                </p>
              </div>
              <button
                type="button"
                onClick={copyCreds}
                data-testid="staff-copy-creds"
                className="mt-4 inline-flex items-center gap-2 px-3 h-9 text-xs uppercase tracking-[0.18em] border border-amber-300 hover:border-amber-500 transition-colors bg-white"
              >
                <Copy className="w-3.5 h-3.5" /> {t.copy}
              </button>
            </div>
          )}
        </form>

        {/* LIST */}
        <div className="lg:col-span-7">
          <div className="border border-slate-200 bg-white">
            <div className="px-6 py-4 border-b border-slate-200">
              <p className="eyebrow">{t.list}</p>
            </div>
            {users.length === 0 ? (
              <div className="p-10 text-center text-slate-500 text-sm">{t.listEmpty}</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {users.map((u) => (
                  <li
                    key={u.id}
                    data-testid={`user-row-${u.id}`}
                    className="flex items-center justify-between gap-4 px-6 py-4"
                  >
                    <div className="min-w-0">
                      <p className="font-medium truncate">{u.name}</p>
                      <p className="text-sm text-slate-500 truncate">{u.email}</p>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <span className="text-xs uppercase tracking-[0.18em] text-slate-500">
                        {ROLE_LABELS[u.role]?.[lang] || u.role}
                      </span>
                      {u.id !== user.id && (
                        <button
                          type="button"
                          onClick={() => onDelete(u.id)}
                          data-testid={`user-delete-${u.id}`}
                          className="text-slate-400 hover:text-red-600 transition-colors"
                          aria-label={t.delete}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Google Calendar admin panel */}
      <CalendarAdminPanel isAr={isAr} />
    </div>
  );
}

// ---------- Google Calendar admin panel ----------
function CalendarAdminPanel({ isAr }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");

  const refresh = useCallback(() => {
    api.get("/admin/calendar/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ configured: false, authorized: false }));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ?calendar=connected | error after redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const c = params.get("calendar");
    if (c === "connected") {
      setFlash(isAr ? "تم ربط تقويم Google بنجاح." : "Google Calendar connected.");
      // strip the param so a refresh doesn't keep showing the flash
      window.history.replaceState({}, "", window.location.pathname);
    } else if (c === "error") {
      setFlash(isAr ? "تعذّر الربط بتقويم Google." : "Could not connect to Google Calendar.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [isAr]);

  const onConnect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/calendar/connect");
      window.location.href = data.authorization_url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start OAuth.");
      setBusy(false);
    }
  };

  const onDisconnect = async () => {
    if (!window.confirm(isAr ? "إلغاء ربط التقويم؟" : "Disconnect calendar?")) return;
    setBusy(true);
    try {
      await api.post("/admin/calendar/disconnect");
      refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-slate-200 bg-white p-6 mt-12 max-w-3xl" data-testid="calendar-admin-panel">
      <p className="eyebrow">{isAr ? "تقويم Google للحجوزات" : "Google Calendar for bookings"}</p>
      <h3 className="font-display text-2xl mt-2">
        {isAr ? "زامن مواعيد الاستشارات تلقائياً" : "Auto-sync paid consultation bookings"}
      </h3>
      <p className="mt-3 text-sm text-slate-600 leading-relaxed">
        {isAr
          ? "بمجرد أن يدفع العميل عبر MyFatoorah، يُنشأ موعد على تقويم المكتب تلقائياً، مع رابط Google Meet ودعوة لبريد العميل."
          : "Once a client pays via MyFatoorah, a calendar event is created on the firm's calendar — including a Google Meet link and an invitation to the client's email."}
      </p>

      {flash ? (
        <div className="mt-4 px-4 py-3 border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm">
          {flash}
        </div>
      ) : null}

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        {status === null ? (
          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        ) : !status.configured ? (
          <div className="text-sm text-slate-700">
            {isAr
              ? "للبدء: أضف GOOGLE_CLIENT_ID و GOOGLE_CLIENT_SECRET في إعدادات الخادم."
              : "To enable, set GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET in the backend .env."}
          </div>
        ) : status.authorized ? (
          <>
            <span className="px-2 py-0.5 text-xs uppercase tracking-[0.18em] border border-emerald-200 bg-emerald-50 text-emerald-700">
              {isAr ? "مُفعّل" : "Connected"}
            </span>
            <Button
              type="button"
              onClick={onDisconnect}
              disabled={busy}
              data-testid="calendar-disconnect"
              variant="outline"
              className="rounded-sm h-10"
            >
              {isAr ? "إلغاء الربط" : "Disconnect"}
            </Button>
          </>
        ) : (
          <Button
            type="button"
            onClick={onConnect}
            disabled={busy}
            data-testid="calendar-connect"
            className="rounded-sm h-10 bg-slate-900 hover:bg-slate-800 text-white"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {isAr ? "اربط تقويم Google" : "Connect Google Calendar"}
          </Button>
        )}
      </div>
    </div>
  );
}
